/**
 * "Is this a phone?", answered once and shared.
 *
 * The trainer's phone layout is CSS — order, a pinned action bar, a folded setup
 * column — and CSS alone would have been the whole change, except for one thing: a
 * <details> cannot be forced open by a stylesheet. On a wide screen the setup column
 * is not a disclosure at all, it is just a column, so the component has to know which
 * of the two it is rendering.
 *
 * ONE BREAKPOINT, WRITTEN ONCE. `COMPACT` is the same string as the media query in
 * `app.css`, and a test asserts they match — two copies of a breakpoint that drift is
 * a layout that is half phone and half desktop for a band of widths nobody tests at.
 */
import { useSyncExternalStore } from "react";

export const COMPACT = "(max-width: 52rem)";

/**
 * `useSyncExternalStore` rather than `useEffect` + `useState`.
 *
 * The effect version renders once at the wrong size and then corrects itself, which
 * for this particular flag means the setup column flashes open and slams shut on
 * every load. The store version is right on the first paint.
 */
export function useCompact(): boolean {
  return useSyncExternalStore(subscribe, snapshot, () => false);
}

const mql = (): MediaQueryList | null =>
  typeof window === "undefined" || !window.matchMedia ? null : window.matchMedia(COMPACT);

function subscribe(onChange: () => void): () => void {
  const m = mql();
  if (!m) return () => {};
  m.addEventListener("change", onChange);
  return () => m.removeEventListener("change", onChange);
}

function snapshot(): boolean {
  return mql()?.matches ?? false;
}
